import * as React from 'react';
import Dropdown from '../../Dropdown/Dropdown';
import type { ReferenceFilterItem, FilterItem, ListReferenceFieldFilterItem, ReferenceFieldFilterItem } from '../../../../types/Filter';
import { AddFilterDropdown } from '../AddFilter/AddFilter';
import type { OnSelectObject } from '../../../studio/AddReferenceModal/SelectExistingEntityModal/SelectExistingEntityModal';
import SelectExistingEntity from '../../../studio/AddReferenceModal/SelectExistingEntityModal/SelectExistingEntityModal';
import TokenInput from '../../TokenInput/TokenInput';
import type { OnFilterChangeHandler } from '../types';
import { findObjectInList } from '../../../../modules/studio/utils-ts';
import ModalComponent from '../../Modal/Modal';
import { makeSingular, pluralize } from '../../../../modules/utils';
import DeleteFilterButton from './DeleteFilterButton';
import Styles from './filter-views.module.scss';

type Props = {
    filterItem: ReferenceFilterItem;
    availableFilters: FilterItem[];
    onChange: OnFilterChangeHandler;
    selectedLocale: string | null;
};

const DROPDOWN_ITEMS = [
    { name: 'One of', value: 'in' },
    { name: 'Is not one of', value: 'nin' },
    { name: 'Is set', value: 'is-not-undefined' },
    { name: 'Not set', value: 'is-undefined' }
];

const DROPDOWN_ITEMS_LIST = [
    { name: 'One of', value: 'in' },
    { name: 'Is not one of', value: 'nin' },
    { name: 'Is all', value: 'all' },
    { name: 'Is empty', value: 'is-empty' },
    { name: 'Not empty', value: 'is-not-empty' }
];

const ReferenceFilterView = (props: Props) => {
    const { filterItem } = props;

    const [selectModalVisible, setSelectModalVisible] = React.useState(false);
    const hasValues = filterItem.operator === 'in' || filterItem.operator === 'nin' || filterItem.operator === 'all';

    const selectedOperatorItem = DROPDOWN_ITEMS_LIST.find((item) => item.value === filterItem.operator);

    const { fieldModel } = filterItem.fieldProps;
    const modelLabel = fieldModel.label ?? fieldModel.name;
    const placeholder = `Select ${pluralize(2, makeSingular(modelLabel.toLowerCase()), { useCount: false })}`;

    return (
        <div className={Styles['filter-view']}>
            <AddFilterDropdown
                availableFilters={props.availableFilters}
                value={filterItem}
                onAdd={(filterItem) => filterItem !== props.filterItem && props.onChange(filterItem)}
            />
            <Dropdown
                options={filterItem.isList ? DROPDOWN_ITEMS_LIST : DROPDOWN_ITEMS}
                value={filterItem.operator}
                menuClassName={Styles['filter-view-dropdown-tooltip']}
                className={Styles.control}
                onChange={(operator) => {
                    if (operator !== filterItem.operator) {
                        const existingValues = 'values' in filterItem ? filterItem.values : undefined;
                        const isNewOpSimple =
                            operator === 'is-undefined' || operator === 'is-not-undefined' || operator === 'is-empty' || operator === 'is-not-empty';

                        if (filterItem.isList) {
                            props.onChange({
                                ...filterItem,
                                operator: operator as ListReferenceFieldFilterItem['operator'],
                                values: isNewOpSimple ? undefined : existingValues
                            });
                        } else {
                            props.onChange({
                                ...filterItem,
                                operator: operator as ReferenceFieldFilterItem['operator'],
                                values: isNewOpSimple ? undefined : existingValues
                            });
                        }
                    }
                }}
                dataTip={selectedOperatorItem?.name}
                dataTipOverflow
            />

            {hasValues ? (
                <>
                    <TokenInput
                        className={Styles.control}
                        values={
                            filterItem.values?.map((object) => {
                                const collectionObject = findObjectInList(filterItem.fieldProps.objects, object);
                                return {
                                    title: collectionObject?.srcObjectLabel ?? ''
                                };
                            }) ?? []
                        }
                        onClick={() => {
                            setSelectModalVisible(true);
                        }}
                        onRemove={(index) => {
                            const newValues = [...(filterItem.values ?? [])];
                            newValues.splice(index, 1);
                            props.onChange({
                                ...filterItem,
                                values: newValues
                            });
                        }}
                        placeholder={placeholder}
                    />
                    {selectModalVisible && (
                        <SelectExistingEntity
                            fieldModel={filterItem.fieldProps.fieldModel}
                            models={filterItem.fieldProps.models}
                            srcType={filterItem.fieldProps.rootModel.srcType}
                            srcProjectId={filterItem.fieldProps.rootModel.srcProjectId}
                            schemaModels={filterItem.fieldProps.schemaModels}
                            collections={filterItem.fieldProps.collections}
                            FiltersSearchView={filterItem.fieldProps.FiltersSearchView}
                            Modal={ModalComponent}
                            titlePrefix="Select"
                            buttonText="Select"
                            onClose={() => setSelectModalVisible(false)}
                            multiple
                            selected={filterItem.values ?? []}
                            onSelect={(objects: OnSelectObject | OnSelectObject[]) => {
                                props.onChange({
                                    ...filterItem,
                                    values: (Array.isArray(objects) ? objects : [objects]).map((object) => ({
                                        srcType: object.model.srcType,
                                        srcProjectId: object.model.srcProjectId,
                                        srcObjectId: object.srcObjectId
                                    }))
                                });
                                setSelectModalVisible(false);
                                return Promise.resolve();
                            }}
                            selectedLocale={props.selectedLocale}
                        />
                    )}
                </>
            ) : (
                <div /> /* just empty DIV for grid layout */
            )}

            <DeleteFilterButton {...props} />
        </div>
    );
};

export default ReferenceFilterView;
